import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ProjectMembers from "./ProjectMembers";
import TaskBoard from "../tasks/TaskBoard";
import {
  FaCalendarAlt,
  FaUser,
  FaEdit,
  FaTrash,
  FaTasks,
  FaCheck,
} from "react-icons/fa";
import EditProjectModal from "../ProjectModals/EditProjectModal";
import DeleteProjectModal from "../ProjectModals/DeleteProjectModal";
import AssignTasksModal from "../ProjectModals/AssignTasksModal";
import AddMemberModal from "../ProjectModals/AddMemberModal";

const ProjectDetails = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState("Guest");
  const [userId, setUserId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectMembers, setProjectMembers] = useState([]);
  const [showAssignTasksModal, setShowAssignTasksModal] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [taskBeingEdited, setTaskBeingEdited] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [projectRole, setProjectRole] = useState(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const triggerRefresh = () => setRefreshKey((prev) => prev + 1);

  const [taskRefreshKey, setTaskRefreshKey] = useState(0);
  const triggerTaskRefresh = () => setTaskRefreshKey((prev) => prev + 1);

  // const handleTaskCreated = (newTask) => {
  //   setTasks((prevTasks) => [...prevTasks, newTask]);
  // };

  const handleTaskUpdated = (updatedTask) => {
    setTasks((prevTasks) =>
      prevTasks.map((task) => (task.id === updatedTask.id ? updatedTask : task))
    );
  };

  const handleTaskDeleted = (taskId) => {
    setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
  };

  useEffect(() => {
    const fetchMembers = async () => {
      const token = localStorage.getItem("auth_token");

      try {
        const res = await fetch(
          `https://loopsneller.com/api/projects/${id}/members`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!res.ok) {
          throw new Error("Failed to fetch project members");
        }

        const data = await res.json();
        setProjectMembers(data);

        const currentUser = data.find((member) => member.user_id === userId);

        if (currentUser) {
          setProjectRole(currentUser.role);
        }
      } catch (error) {
        console.error("Error fetching members:", error.message);
        setProjectMembers([]);
      }
    };

    if (userId) {
      fetchMembers();
    }
  }, [id, userId]);

  useEffect(() => {
    const authToken = localStorage.getItem("auth_token");
    const userData = localStorage.getItem("user");

    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUserId(parsedUser.id); // needed for isCreator
        setUserRole(parsedUser.role || "Guest"); // GLOBAL role, keep this!
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }

    fetch(`https://loopsneller.com/api/projects/${id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch project details");
        }
        return response.json();
      })
      .then((data) => {
        setProject(data);
        setLoading(false);
      })
      .catch((error) => {
        setError(error.message);
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (userId && projectMembers.length > 0) {
      const member = projectMembers.find((m) => m.user.id === userId);
      if (member) {
        setProjectRole(member.role);
      } else {
        setProjectRole("Guest");
      }
    }
  }, [userId, projectMembers]);

  if (loading) return <p className="text-center mt-4">Loading...</p>;
  if (error)
    return <p className="text-danger text-center mt-4">Error: {error}</p>;

  // Derived permission flags
  const isCreator = userId === project.created_by;
  const isAssigned = project.members?.some((m) => m.id === userId);
  const isAssignedManager = project.members?.some(
    (m) => m.id === userId && m.pivot?.role === "Manager"
  );

  return (
    <div className="container mt-4">
      <div className="card shadow-lg p-4 rounded">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="mb-0">{project?.name}</h2>
          </div>
          {projectRole && (
            <span className="badge bg-info fs-5">Your Role: {projectRole}</span>
          )}
        </div>

        {/* Project Info */}
        <div className="row">
          <div className="col-md-6">
            <p className="text-muted">
              {project.description || "No description provided."}
            </p>
          </div>
          <div className="col-md-6">
            <div className="mb-2">
              <FaCalendarAlt className="me-2 text-primary" />
              <strong>Start Date:</strong> {project.start_date || "N/A"}
            </div>
            <div className="mb-2">
              <FaCalendarAlt className="me-2 text-danger" />
              <strong>End Date:</strong> {project.end_date || "N/A"}
            </div>
            <div className="mb-2">
              <FaUser className="me-2 text-secondary" />
              <strong>Created By:</strong>{" "}
              {project.creator?.name ?? `User ID ${project.created_by}`}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="card-footer bg-white border-0 mt-3 d-flex flex-wrap gap-3">
          {(userRole === "Admin" || isCreator) && (
            <>
              <button
                className="btn btn-danger"
                onClick={() => setShowDeleteModal(true)}
              >
                <FaTrash className="me-2" /> Delete Project
              </button>
              <button
                className="btn btn-warning text-dark"
                onClick={() => setShowEditModal(true)}
              >
                <FaEdit className="me-2" /> Edit Project
              </button>
            </>
          )}

          {(userRole === "Admin" ||
            userRole === "Manager" ||
            isAssignedManager) && (
            <button
              className="btn btn-primary"
              onClick={() => setShowAssignTasksModal(true)}
            >
              <FaTasks className="me-2" /> Manage Tasks
            </button>
          )}

          {(userRole === "Admin" ||
            userRole === "Manager" ||
            isAssignedManager) && (
            <button
              className="btn btn-primary"
              onClick={() => setShowAddMember(true)}
            >
              <FaTasks className="me-2" /> Manage Members
            </button>
          )}

          {userRole === "Employee" && isAssigned && (
            <button className="btn btn-success">
              <FaCheck className="me-2" /> Mark as Completed
            </button>
          )}
        </div>
      </div>

      {/* Project Members & Task Board */}
      <ProjectMembers projectId={id} refreshKey={refreshKey} />
      <br />
      <TaskBoard projectId={id} refreshKey={taskRefreshKey} />
      <EditProjectModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        project={project}
        onProjectUpdated={(updatedProject) => setProject(updatedProject)}
      />
      <DeleteProjectModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        project={project}
        members={projectMembers}
      />
      <AssignTasksModal
        show={showAssignTasksModal}
        onClose={() => setShowAssignTasksModal(false)}
        projectId={id}
        editingTask={taskBeingEdited} // null when creating new task
        onTaskCreated={triggerTaskRefresh}
        onTaskUpdated={handleTaskUpdated}
        onTaskDeleted={handleTaskDeleted}
      />
      <AddMemberModal
        show={showAddMember}
        onClose={() => setShowAddMember(false)}
        projectId={id}
        onMemberAdded={triggerRefresh}
      />
    </div>
  );
};

export default ProjectDetails;
